import React, { useState, useRef, useReducer } from "react"
import isEqual from 'react-fast-compare'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { reducerDeepCompare } from '../utils/useStateDeepEqual'

import {
  dataStore,
  mapStore,
  locationStore,
  pageStore,
  filterStore
} from "state/store-zustand";

import { featureCollection } from "@turf/helpers"

import ReactMapGL, {
  Layer,
  Marker,
  Popup,
  Source
} from "!react-map-gl"
import "mapbox-gl/dist/mapbox-gl.css"

import mapboxgl from "mapbox-gl"; // This is a dependency of react-map-gl even if you didn't explicitly install it
// eslint-disable-next-line import/no-webpack-loader-syntax
//mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

import MarkerImage from "./marker/markerImage"
import Markers from './markers'
import Pin from './marker/pin'

import { getClusters, getFeaturesInBounds } from 'vibemap-constants/dist/map.js'

import { boundsFromPlaces, viewportFromBounds } from './mapUtils'
import mapStyles from './map_styles.js'
import "./map.scss"

const BasicMap = ({
  loading = false,
  link = null,
  openInApp = true,
  boundsToMarkers = false,
  dragPan = true,
  dragPanEnabled = true, // Override drag behavior to handle card touch events
  fitPlacesToBounds = true,
  heatmap = false,
  height = 400,
  latitude,
  longitude,
  loadFromTile = false,
  mapOptions = {
    interactive: true
  },
  mapTheme,
  markerStyle = 'icons',
  numMarkers = 32,
  map3d = false,
  offsetTop = 0,
  showDraggableMarker = true,
  showExplore = true,
  showFullScreen = true,
  showEvents = true, // TODO: set from filter state if All or Events is selected
  showMarkers = true, // TODO: Make it more clear what is photo markers vs place markers
  showMarkerLabel = true,
  showPlaces = true,
  placesLayer = [],
  width = 420,
  bearing = 0,
  pitch = 10,
  scrollZoom = true,
  showCards = true, // TODO: Update from Wordpress options
  zoom = mapStyles.MAPBOX_ZOOM_DEFAULT,
  ...props
}) => {

  // Mapbox reference for controls and the like
  const mapRef = props.mapRef
    ? props.mapRef
    : useRef(null)

  const getMap = () => {
    const map = mapRef?.current
      ? mapRef.current.getMap()
      : null
    return map
  }

  const map = getMap()
  const bounds = map?.getBounds()
  const center = map?.getCenter()

  const [clusterSize, setClusterSize] = useState(200)
  const [settings, setSettings] = useState(mapStyles.mapbox_settings)

  const isMobile = pageStore((state) => state.isMobile)
  const placeCurrent = dataStore((state) => state.placeCurrent)
  const places = dataStore((state) => state.places)
  const numPlaces = dataStore((state) => state.numPlaces)
  const editorialCategory = filterStore((state) => state.editorialCategory)

  const locationCurrent = locationStore((state) => state.locationCurrent)
  const currentLatitude = latitude ? latitude : locationCurrent?.latitude
  const currentLongitude = longitude ? longitude : locationCurrent?.longitude
  const zoomCurrent = locationStore((state) => state.zoomCurrent)

  const latitudeMap = currentLatitude ? currentLatitude : props.latitude
  const longitudeMap = currentLongitude ? currentLongitude : props.longitude

  const [viewport, setViewport] = useReducer(reducerDeepCompare, {
    width: width,
    height: height,
    bearing: bearing,
    pitch: pitch,
    latitude: latitudeMap,
    longitude: longitudeMap,
    showPopup: true,
    zoom: zoomCurrent
      ? zoomCurrent
      : props.zoom
  })

  const [marker, setMarker] = useReducer(reducerDeepCompare, {
    latitude: latitudeMap,
    longitude: longitudeMap
  })

  const [placeMarkers, setPlaceMarkers] = useReducer(reducerDeepCompare, [...places])
  const [placesGeoJSON, setPlacesGeoJSON] = useReducer(reducerDeepCompare, {
    type: "FeatureCollection",
    features: [],
  })

  const [popupInfo, setPopupInfo] = useState(null)
  //const [placesClustered, setPlacesClustered] = useReducer(reducerDeepCompare, [])
  const [shouldUpdateMarkers, setShouldUpdateMarkers] = useState(true)

  // Create Markers from places
  // And update bounds to fit places
  useDeepCompareEffect(() => {
    const map = getMap()
    const bounds = map?.getBounds()?.toArray()

    // Make GeoJSON for markers layer
    if (places && places?.length > 0) {
      let geojson = featureCollection(places)
      setPlaceMarkers(places)
      setPlacesGeoJSON(geojson)

      if (boundsToMarkers) {
        try {
          const expandedBounds = boundsFromPlaces(geojson, bounds)
          const newViewport = viewportFromBounds(expandedBounds, viewport)
          setViewport(newViewport)
        } catch (error) {
          console.log('DEBUG: problem with bounds');
        }
      }
    }

    // Make features data for photo markers layer
    const hasMapAndBounds = bounds || map

    if (showMarkers && shouldUpdateMarkers && hasMapAndBounds) {

      // If map has strict bounds, update places to only fit map viewport/bounds
      // Update places to only fit map viewport/bounds

      const placesInBounds = fitPlacesToBounds
        ? getFeaturesInBounds(places, bounds)
        : places

      // TODO: Or if the map has the fitBoundsFromPlaces option, set the bounds from places
      // Should come from global context
      //console.log('DEBUG: Places in bounds: ', placesInBounds, bounds, places);
      //setPlaces(placesInBounds)

      try {
        const numOfMarkers = numPlaces > numMarkers ? numMarkers : numPlaces
        //const placesTop = placesInBounds.slice(0, numOfMarkers) // Only show top
        const placesClustered = getClusters(placesInBounds, clusterSize)
        //const markersUpdated = getMarkers(placesClustered, false, false)

      } catch (error) {
        console.log('DEBUG: problem with markers ', error);
      }
    }

  }, [places, zoomCurrent, map, bounds])

  // Handle Map Events
  // TODO: Move to separate, shared component
  const _onViewportChange = (new_viewport, event) => {
    // Handle user interactions
    if (event?.isDragging) {
      //handleDragEnd(event)
    }

    if (event?.isPanning || event?.isZooming) {
      //handleZoomEnd(event)
    }

    const viewport_changed = !isEqual(viewport, new_viewport)

    // Keep internal map state
    if (viewport_changed) {
      // FIXME: why does the zoom need overriden?
      setViewport({
        ...new_viewport,
        zoom
      })
      // And call callback, if provided
      if ("onViewportChange" in props) props.onViewportChange(viewport)
    }
  }

  const handleOnMouseOver = (_event, feature) => {
    showPopup(feature.properties.name, feature.geometry.coordinates[1], feature.geometry.coordinates[0])
  }

  const handleOnMouseOut = () => {
    setPopupInfo(null)
  }

  const showPopup = (name, latitude, longitude) => {
    setPopupInfo({
      name: name,
      latitude: latitudeMap,
      longitude: longitudeMap
    })
  }

  // Handle events
  // Marker on change
  const onChange = (event) => {
    console.log('onChange ', event)

    setMarker({ longitude: event.lngLat[0], latitude: event.lngLat[1] });
    props.onChange(event)
  }

  // Map styles & layout
  // TODO: set in state?
  let theme = mapStyles.MAPBOX_STYLE
  if (mapTheme === 'gray') theme = mapStyles.MAPBOX_STYLE_GRAY
  if (mapTheme === 'outdoors') theme = mapStyles.MAPBOX_STYLE_GRAY

  const widthStyle = { maxWidth: '100%' }
  const mapContainerClass = `post-map-viewport`

  let query = {
    latitude: center?.lat,
    longitude: center?.lng,
    zoom: zoom
  }
  // Don't include null params
  if (editorialCategory) query.editorialCategory = editorialCategory

  const params = new URLSearchParams(query)
  const isBitlyCustom = link?.includes('on.vibemap.com')
  const exploreLink = isBitlyCustom ? link : link + params.toString()

  // Fix me store currentPlace in consistent format
  const images = placeCurrent?.properties
    ? placeCurrent?.properties?.vibemap_images
    : placeCurrent?.vibemap_images

  const imageSrc = images?.length > 0 ? images[0].thumbnail_url : null

  const mapStyle = {
    height: height,
    width: width,
    maxWidth: '100%'
  }

  const exploreButtonStyle = {
    position: 'absolute',
    bottom: 6,
    right: 6
  }

  const mapZoom = viewport.zoom
    ? viewport.zoom
    : zoom

  return (
    <div className={mapContainerClass}>
      <ReactMapGL
        {...viewport}
        {...settings}
        ref={mapRef}
        mapboxAccessToken={mapStyles.MAPBOX_TOKEN}
        mapStyle={theme}
        className='react-map-gl'
        mapOptions={mapOptions}
        dragPan={dragPan}
        touchAction={isMobile ? 'none' : 'pan-y'}
        onViewportChange={_onViewportChange}
        //onHover={_onHover}
        //onLoad={mapLoaded}
        //onData={mapLoaded}
        style={mapStyle}
        height={height}
        width={width}
        scrollZoom={scrollZoom}
        zoom={mapZoom}
      >
        {props.showDraggableMarker &&
          <Marker
            longitude={longitudeMap}
            latitude={latitudeMap}
            offsetTop={-20}
            offsetLeft={-10}
            draggable
            //onDragStart={onMarkerDragStart}
            //onDrag={onMarkerDrag}
            onDragEnd={onChange}
          >
            <Pin height={40} width={32} />
          </Marker>
        }

        {props.showFeaturedMarker
          ? <MarkerImage
              latitude={latitudeMap}
              longitude={longitudeMap}
              feature={placeCurrent}
              imageSrc={imageSrc} />
          : null
        }

        {showMarkers && showPlaces
          ? <Markers
              bounds={bounds}
              features={placeMarkers}
              handleOnMouseOver={handleOnMouseOver}
              handleOnMouseOut={handleOnMouseOut} />
          : null}

        {popupInfo &&
          <Popup
            //tipSize={1}
            className={'marker-popup'}
            //offsetTop={-10}
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            closeButton={false}>
            {popupInfo.name}
          </Popup>
        }

        <Source type="geojson" data={placesGeoJSON ? placesGeoJSON : emptySource}>
          {showMarkers
            ? <Layer
              id='markers'
              type="symbol"
              layout={mapStyles.marker_layout}
              paint={mapStyles.marker_paint}
            />
            : null}
        </Source>

        <div className="children">
          {props.children}
        </div>
      </ReactMapGL>

      {showExplore &&
        <a
          className="ui button small"
          href={openInApp ? exploreLink : 'map'}
          target="_blank"
          style={exploreButtonStyle}>
          Explore Map
        </a>
      }
    </div>
  )
}

//Map.whyDidYouRender = true
export default React.memo(BasicMap);